import { apiGet } from './api';

/**
 * Fetch a page of carrier-engagement referrals from /api/v1/referrals/.
 *
 * The endpoint is portal-agnostic — security and scoping are derived from
 * request.user. CE sends its own filter set so the backend doesn't need to
 * know which UI is calling.
 *
 * @param {Object} opts
 * @param {string} [opts.cursor]   - Opaque cursor from a prior page's next_page.
 *                                   Omit for page 1.
 * @param {number} [opts.limit]    - Page size (server caps at 1000).
 * @param {string} [opts.search]   - Free-text search (id + referral_name).
 * @param {Object} [opts.filters]  - Additional filter params keyed by the
 *                                   backend's filter names (client_id,
 *                                   tpa_id, aging_out, referral_type, …).
 *                                   tpa_id is the backend's name for what
 *                                   product/UI calls "partner."
 * @returns {Promise<{total_records:number, returned_records:number,
 *                    next_page:string|null, results:Array}>}
 */
export const fetchEngagementReferrals = async ({
  cursor,
  limit,
  search,
  filters = {},
} = {}) => {
  const params = new URLSearchParams();

  // CE always restricts to active OM clients and excludes the demo client.
  params.set('product_type', 'Ongoing Monitoring');
  params.set('active_clients_only', 'true');
  params.set('exclude_demo', 'true');
  // CE is the SmartLight-internal dashboard:
  // - include internal (SL) comments in the last-comment column
  // - include inactive/closed referrals (management overview view)
  // CP omits both params (external-only, active-only defaults).
  params.set('include_internal_comments', 'true');
  params.set('include_inactive', 'true');

  if (cursor) params.set('cursor', cursor);
  if (limit) params.set('limit', String(limit));
  if (search) params.set('search', search);
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });

  return apiGet(`/api/v1/referrals/?${params.toString()}`);
};
